<script setup lang="ts">
import {
  DeleteKioskGame,
  GAMES_USE_MOCKS,
  GetGamePrizes,
  GetGamesSummary,
  GetKioskGames,
  SaveKioskGame,
  SetKioskGameStatus,
} from "@/api/gamesApi";
import ConfirmDeleteDialog from "@/components/dialogs/ConfirmDeleteDialog.vue";
import GameFormDialog from "@/components/games/GameFormDialog.vue";
import GamesDemoAlert from "@/components/games/GamesDemoAlert.vue";
import GamesKpiStrip from "@/components/games/GamesKpiStrip.vue";
import EntityListTable from "@/components/shared/EntityListTable.vue";
import EntityListToolbar from "@/components/shared/EntityListToolbar.vue";
import { useGamesFormat } from "@/composables/useGamesFormat";
import { GAMES_ADMIN_ROLES } from "@/config/moduleRegistry/keys";
import { canAnyRole, canDoctype } from "@/helper/permissionHelper";
import useToastify from "@/stores/toastify.store";
import {
  emptyKioskGame,
  gameStatus,
  gameStatusColor,
  gameTypeIcon,
  type AddEditKioskGame,
  type GamePrize,
  type GameStatus,
  type GamesSummary,
  type KioskGame,
} from "@/type/games";

/**
 * Kiosk games — every campaign, its state today, and a way into its odds.
 * A game shows on the kiosk only while its effective status is Active: stored
 * Active AND today inside its dates.
 */

definePage({ meta: { permissionKey: "page.games.list" } });

const { t } = useI18n({ useScope: "global" });
const router = useRouter();
const toast = useToastify();
const { gameTitle, gameTypeLabel, gameStatusLabel, formatDate, formatInteger } = useGamesFormat();

const games = ref<KioskGame[]>([]);
const prizes = ref<GamePrize[]>([]);
const loading = ref(false);
const failed = ref(false);

const summary = ref<GamesSummary | null>(null);
const summaryLoading = ref(false);
const summaryFailed = ref(false);

/** On the demo store the backend has no doctype to check, so page access is the gate. */
const canManage = computed(() => GAMES_USE_MOCKS || canDoctype("Kiosk Game", "write") || canAnyRole(GAMES_ADMIN_ROLES));
const canDelete = computed(() => GAMES_USE_MOCKS || canDoctype("Kiosk Game", "delete") || canAnyRole(GAMES_ADMIN_ROLES));

async function load() {
  failed.value = false;
  try {
    const [gameRows, prizeRows] = await Promise.all([GetKioskGames(loading), GetGamePrizes(ref(false))]);
    games.value = gameRows;
    prizes.value = prizeRows;
  }
  catch {
    failed.value = true;
  }
  void loadSummary();
}

async function loadSummary() {
  summaryFailed.value = false;
  try {
    summary.value = await GetGamesSummary(summaryLoading);
  }
  catch {
    summaryFailed.value = true;
  }
}

// --- Filters -------------------------------------------------------------------

const search = ref("");
const statusFilter = ref<GameStatus | null>(null);

const statusItems = computed(() => (["active", "scheduled", "paused", "draft", "ended"] as GameStatus[])
  .map(value => ({ value, title: gameStatusLabel(value) })));

const rows = computed(() => {
  const q = search.value.trim().toLowerCase();

  return games.value
    .map(game => ({ ...game, effectiveStatus: gameStatus(game) }))
    .filter(game => !statusFilter.value || game.effectiveStatus === statusFilter.value)
    .filter(game => !q || `${game.title} ${game.title_ar}`.toLowerCase().includes(q));
});

const headers = computed(() => [
  { title: t("alkokh.games.columns.game"), key: "title" },
  { title: t("alkokh.games.columns.type"), key: "game_type" },
  { title: t("alkokh.games.columns.status"), key: "effectiveStatus" },
  { title: t("alkokh.games.columns.dates"), key: "start_date", sortable: false },
  { title: t("alkokh.games.columns.prizes"), key: "prizes", sortable: false, align: "end" as const },
  { title: t("alkokh.global.actions"), key: "actions", sortable: false, align: "end" as const },
]);

// --- Create / edit ---------------------------------------------------------------

const formOpen = ref(false);
const formGame = ref<AddEditKioskGame>(emptyKioskGame());
const formOriginal = ref<KioskGame | null>(null);

function openAdd() {
  formOriginal.value = null;
  formGame.value = emptyKioskGame();
  formOpen.value = true;
}

function openEdit(game: KioskGame) {
  formOriginal.value = game;
  formGame.value = JSON.parse(JSON.stringify(game));
  formOpen.value = true;
}

async function onSaved(saved: KioskGame) {
  const isNew = !formOriginal.value;
  await load();
  // A new game has no prizes yet; take the admin straight to where they are added.
  if (isNew)
    await router.push({ path: `/games/${encodeURIComponent(saved.name)}`, query: { tab: "prizes" } });
}

// --- Row actions -------------------------------------------------------------------

const busyRow = ref<string | null>(null);

async function toggleStatus(game: KioskGame) {
  if (game.status !== "active" && !game.prizes.length) {
    toast.warning({ text: t("alkokh.games.validation.activeNeedsPrizes") });

    return;
  }
  busyRow.value = game.name;
  try {
    const next = game.status === "active" ? "paused" : "active";
    await SetKioskGameStatus(ref(false), game.name, next);
    toast.success({ text: t(next === "active" ? "alkokh.games.messages.activated" : "alkokh.games.messages.paused") });
    await load();
  }
  catch (error: any) {
    toast.error({ text: error?.message || t("alkokh.games.messages.saveFailed") });
  }
  finally {
    busyRow.value = null;
  }
}

async function duplicate(game: KioskGame) {
  busyRow.value = game.name;
  try {
    const copy: AddEditKioskGame = {
      ...JSON.parse(JSON.stringify(game)),
      name: undefined,
      // Each title is renamed in its own language, whatever language the admin is using.
      title: t("alkokh.games.list.copyOf", { name: game.title }, { locale: "en" }),
      title_ar: game.title_ar ? t("alkokh.games.list.copyOf", { name: game.title_ar }, { locale: "ar" }) : "",
      status: "draft",
    };
    const saved = await SaveKioskGame(ref(false), copy);
    toast.success({ text: t("alkokh.games.messages.duplicated") });
    await load();
    openEdit(saved);
  }
  catch (error: any) {
    toast.error({ text: error?.message || t("alkokh.games.messages.saveFailed") });
  }
  finally {
    busyRow.value = null;
  }
}

const deleteOpen = ref(false);
const pendingDelete = ref<KioskGame | null>(null);

function requestDelete(game: KioskGame) {
  pendingDelete.value = game;
  deleteOpen.value = true;
}

async function confirmDelete() {
  const game = pendingDelete.value;
  deleteOpen.value = false;
  pendingDelete.value = null;
  if (!game)
    return;
  try {
    await DeleteKioskGame(ref(false), game.name);
    toast.success({ text: t("alkokh.games.messages.gameDeleted") });
    await load();
  }
  catch (error: any) {
    toast.error({ text: error?.message || t("alkokh.games.messages.deleteFailed") });
  }
}

onMounted(load);
</script>

<template>
  <div class="games-page">
    <div class="games-page__head">
      <div>
        <h1 class="text-h4">
          {{ t("alkokh.games.list.title") }}
        </h1>
        <p class="text-body-2 text-medium-emphasis mb-0">
          {{ t("alkokh.games.list.subtitle") }}
        </p>
      </div>
      <div class="d-flex flex-wrap gap-2">
        <VBtn
          variant="tonal"
          prepend-icon="tabler-chart-bar"
          to="/games/dashboard"
        >
          {{ t("alkokh.games.dashboard.title") }}
        </VBtn>
        <VBtn
          variant="tonal"
          prepend-icon="tabler-gift"
          to="/games/prizes"
        >
          {{ t("alkokh.games.prizes.title") }}
        </VBtn>
        <VBtn
          v-if="canManage"
          color="primary"
          prepend-icon="tabler-plus"
          @click="openAdd"
        >
          {{ t("alkokh.games.actions.newGame") }}
        </VBtn>
      </div>
    </div>

    <GamesDemoAlert @reset="load" />

    <GamesKpiStrip
      class="mb-4"
      :summary="summary"
      :loading="summaryLoading"
      :failed="summaryFailed"
    />

    <VAlert
      v-if="failed"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      {{ t("alkokh.games.messages.loadFailed") }}
      <template #append>
        <VBtn
          size="small"
          variant="text"
          @click="load"
        >
          {{ t("alkokh.global.retry") }}
        </VBtn>
      </template>
    </VAlert>

    <VCard elevation="0">
      <EntityListToolbar
        :search="search"
        :search-placeholder="t('alkokh.global.search')"
        :show-add-button="false"
        @update:search="search = $event"
      >
        <template #filters>
          <AppSelect
            v-model="statusFilter"
            :items="statusItems"
            clearable
            density="compact"
            :placeholder="t('alkokh.games.list.allStatuses')"
            style="min-inline-size: 180px;"
          />
        </template>
      </EntityListToolbar>

      <VDivider />

      <VCardText>
        <EntityListTable
          :headers="headers"
          :items="rows"
          :items-length="rows.length"
          :loading="loading"
          client
          :items-per-page="10"
          :items-per-page-options="[10, 25, 50]"
          item-value="name"
          :filtered="Boolean(search || statusFilter)"
          :no-data-text="t('alkokh.games.list.empty')"
          class="text-no-wrap"
        >
          <template #item.title="{ item }">
            <RouterLink
              class="games-page__link"
              :to="`/games/${encodeURIComponent(item.name)}`"
            >
              <VAvatar
                size="32"
                color="primary"
                variant="tonal"
                rounded
              >
                <VIcon
                  :icon="gameTypeIcon(item.game_type)"
                  size="18"
                />
              </VAvatar>
              <span>{{ gameTitle(item) }}</span>
            </RouterLink>
          </template>

          <template #item.game_type="{ item }">
            {{ gameTypeLabel(item.game_type) }}
          </template>

          <template #item.effectiveStatus="{ item }">
            <VChip
              :color="gameStatusColor(item.effectiveStatus)"
              size="small"
              label
            >
              {{ gameStatusLabel(item.effectiveStatus) }}
            </VChip>
          </template>

          <template #item.start_date="{ item }">
            <span v-if="!item.start_date && !item.end_date">{{ t("alkokh.games.list.noDates") }}</span>
            <span v-else>{{ formatDate(item.start_date) }} → {{ formatDate(item.end_date) }}</span>
          </template>

          <template #item.prizes="{ item }">
            {{ formatInteger(item.prizes.length) }}
          </template>

          <template #item.actions="{ item }">
            <VBtn
              icon
              variant="text"
              size="small"
              :aria-label="t('alkokh.games.actions.open')"
              :to="`/games/${encodeURIComponent(item.name)}`"
            >
              <VIcon icon="tabler-eye" />
            </VBtn>
            <template v-if="canManage">
              <VBtn
                icon
                variant="text"
                size="small"
                :loading="busyRow === item.name"
                :aria-label="item.status === 'active' ? t('alkokh.games.actions.pause') : t('alkokh.games.actions.activate')"
                :title="item.status === 'active' ? t('alkokh.games.actions.pause') : t('alkokh.games.actions.activate')"
                @click="toggleStatus(item)"
              >
                <VIcon :icon="item.status === 'active' ? 'tabler-player-pause' : 'tabler-player-play'" />
              </VBtn>
              <VBtn
                icon
                variant="text"
                size="small"
                :aria-label="t('alkokh.global.edit')"
                @click="openEdit(item)"
              >
                <VIcon icon="tabler-pencil" />
              </VBtn>
              <VBtn
                icon
                variant="text"
                size="small"
                :aria-label="t('alkokh.games.actions.duplicate')"
                :title="t('alkokh.games.actions.duplicate')"
                @click="duplicate(item)"
              >
                <VIcon icon="tabler-copy" />
              </VBtn>
            </template>
            <VBtn
              v-if="canDelete"
              icon
              variant="text"
              size="small"
              color="error"
              :aria-label="t('alkokh.global.delete')"
              @click="requestDelete(item)"
            >
              <VIcon icon="tabler-trash" />
            </VBtn>
          </template>
        </EntityListTable>
      </VCardText>
    </VCard>

    <GameFormDialog
      v-model="formOpen"
      v-model:game="formGame"
      :prizes="prizes"
      :original="formOriginal"
      @saved="onSaved"
    />

    <ConfirmDeleteDialog
      :is-dialog-visible="deleteOpen"
      :confirm-title="t('alkokh.games.delete.gameTitle')"
      :confirm-msg="t('alkokh.games.delete.gameMessage', { name: pendingDelete?.title ?? '' })"
      @update:is-dialog-visible="deleteOpen = $event"
      @confirm="confirmDelete"
    />
  </div>
</template>

<style scoped>
.games-page__head {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
  margin-block-end: 16px;
}

.games-page__link {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: inherit;
}

.games-page__link:hover {
  color: rgb(var(--v-theme-primary));
}
</style>
